import { Button, DatePicker, Divider, Select, Input, Card } from '@scuf/common';
import useFetch from 'helpers/hooks/useFetch';
import useUser from 'helpers/hooks/useUser';
import { RmaModelLiteMf, Obf, semanas, cauzaRaizLista } from 'models';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSetRecoilState } from 'recoil';
import { RMAMANUFATURA } from 'resources';
import { notificationAtom } from 'resources';
import { Categorias } from 'models';
import { IOption } from '@scuf/common/dist/components/Select/ISelectProps';

interface ChangeStatusProps {
    rmaState: RmaModelLiteMf;
    setRmaState: React.Dispatch<React.SetStateAction<RmaModelLiteMf>>;
}

function OBFAccordion({ rmaState, setRmaState }: ChangeStatusProps): JSX.Element {
    const { t } = useTranslation(['rmamanufatura', 'common']);
    const [data, setData] = useState<Obf>(rmaState.obf ?? {});
    const [listaCategorias, setListaCategorias] = useState<IOption[]>([]);
    const setNotification = useSetRecoilState(notificationAtom);
    const user = useUser();

    const [response, adicionarEditarObf, loadingObf] = useFetch<Obf>({
        url: RMAMANUFATURA.adicionarEditarObf,
        method: 'POST'
    });

    const [responseCategoria, getCategoria, loadingCategoria] = useFetch<{ causaRaiz: string; isPedidoCorresponde: boolean }>({
        url: RMAMANUFATURA.getCategorias,
        method: 'GET'
    });

    useEffect(() => {
        setData(prev => ({
            ...prev,
            ssoId: user.user?.ssoid,
            rmaManufacturingId: rmaState.id
        }));
    }, []);

    useEffect(() => {
        if (response) {
            setData(prev => ({ ...prev, id: response?.data.id }));
        }
    }, [response]);

    useEffect(() => {
        if (responseCategoria) {
            setData(prev => ({ ...prev, categoria: responseCategoria?.data }));
        }
    }, [responseCategoria]);

    async function AdicionarObf() {
        try {
            await adicionarEditarObf(data);
            setNotification(old => ({ ...old, message: 'OBF adicionada/editada com sucesso!', type: 'information' }));
        } catch {
            setNotification(old => ({ ...old, message: 'Erro ao adicionar Obf', type: 'critical' }));
        }
    }

    async function GetCategoria(causaRaiz: string, isPedidoCorresponde: boolean) {
        if (causaRaiz) {
            try {
                await getCategoria({ causaRaiz: causaRaiz, isPedidoCorresponde: isPedidoCorresponde });
            } catch {
                setNotification(old => ({ ...old, message: 'Erro ao buscar categoria!', type: 'information' }));
            }
        }
    }

    return (
        <Card>
            <h2>{t('rmamanufatura:translation.ObfAddEdit')}</h2>
            <Divider />
            <div className="form4Columns">
                <DatePicker
                    indicator="required"
                    type="date"
                    reserveSpace={false}
                    label={t('common:translation.Date')}
                    value={data?.data}
                    displayFormat={'DD/MM/YYYY'}
                    closeOnDocumentClick={true}
                    onChange={(value: Date) => setData({ ...data, data: value })}
                />
                <Select
                    className="select"
                    indicator="required"
                    placeholder={t('rmamanufatura:translation.ObfWeek')}
                    options={semanas}
                    label={t('rmamanufatura:translation.ObfWeek')}
                    onChange={value => setData({ ...data, semana: value })}
                    value={data.semana}
                />
                <Select
                    className="select"
                    indicator="required"
                    placeholder={t('rmamanufatura:translation.Obf')}
                    options={[
                        { value: true, text: 'Sim' },
                        { value: false, text: 'Não' }
                    ]}
                    label={t('rmamanufatura:translation.Obf')}
                    onChange={value => setData({ ...data, obf: value })}
                    value={data.obf}
                />
                <Input
                    indicator="required"
                    placeholder={t('common:translation.Quantity')}
                    label={t('common:translation.Quantity')}
                    type="number"
                    min={0}
                    onChange={value => setData({ ...data, quantidade: Number(value) })}
                    value={data.quantidade?.toString()}
                />
            </div>
            <div className="form4Columns">
                <Select
                    className="select"
                    indicator="required"
                    placeholder={t('rmamanufatura:translation.ObfRootCause')}
                    options={cauzaRaizLista}
                    label={t('rmamanufatura:translation.ObfRootCause')}
                    onChange={value => {
                        setData({ ...data, causaRaiz: value, isPedidoCorresponde: undefined, categoria: '' });
                    }}
                    value={data.causaRaiz}
                />
                <Select
                    className="select"
                    indicator="required"
                    placeholder={t("rmamanufatura:translation.ObfQuestion")}
                    options={[
                        { value: true, text: 'Sim' },
                        { value: false, text: 'Não' }
                    ]}
                    label={t("rmamanufatura:translation.ObfQuestion")}
                    onChange={value => {
                        setData({ ...data, isPedidoCorresponde: value });
                        GetCategoria(data.causaRaiz!, value);
                    }}
                    value={data.isPedidoCorresponde ?? ''}
                    disabled={!data.causaRaiz}
                />
                <Input
                    indicator="required"
                    placeholder={t('rmamanufatura:translation.ObfCategory')}
                    label={t('rmamanufatura:translation.ObfCategory')}
                    onChange={value => setData({ ...data, categoria: value })}
                    value={data.categoria}
                    disabled
                />
            </div>
            <div className="buttonsSearch">
                <Button
                    type="primary"
                    loading={loadingObf || loadingCategoria}
                    content={t('common:actions.Save')}
                    iconPosition="right"
                    onClick={() => {
                        AdicionarObf();
                    }}
                    disabled={
                        !data?.categoria ||
                        !data?.causaRaiz ||
                        !data?.data ||
                        (data?.quantidade === undefined || data?.quantidade === null) ||
                        !data?.semana ||
                        loadingObf ||
                        loadingCategoria
                    }
                />
            </div>
        </Card>
    );
}

export default OBFAccordion;
